import React from 'react'
import throttle from 'lodash/throttle'
import PropTypes from 'prop-types'

export default class FreetextEntry extends React.PureComponent {
  constructor(props) {
    super(props)

    this.state = {
      isNewAnnotation:
        typeof props.annotationContents !== 'string' || props.annotationContents.length < 1,
      isEditing: false,
      isFocused: false,
    }
  }

  setDivRef = (node) => {
    if (node instanceof HTMLElement) {
      this.div = node
    }
  }

  setTextAreaRef = (node) => {
    if (node instanceof HTMLElement) {
      this.textArea = node
    }
  }

  componentDidMount = () => {
    this.setTextareaWidth()
    this.updateTextareaHeight()
    this.updateAnnotationDimensionsThrottled = throttle(this.updateAnnotationDimensions, 15)
    this.div.addEventListener('focusout', this.handleAnnotationSave, true)

    if (!this.props.annotationContents) {
      this.textArea.focus()
      this.setState((state) => ({
        ...state,
        isFocused: true,
      }))
    }
  }

  componentWillUnmount = () => {
    this.div.removeEventListener('focusout', this.handleAnnotationSave, true)
  }

  componentWillReceiveProps(nextProps) {
    this.updateAnnotationDimensionsThrottled()

    if (nextProps.annotationContents && this.state.isNewAnnotation) {
      this.setState((state) => ({
        ...state,
        isNewAnnotation: false,
      }))
    } else if (!nextProps.isSelected && this.props.isSelected) {
      this.handleAnnotationSave()
    }
  }

  updateAnnotationDimensions = () => {
    this.setTextareaWidth()
    this.updateTextareaHeight()
  }

  handleAnnotationSave = (event) => {
    const isInParentDiv = event && this.props.onParentBlur(event) && event.relatedTarget
    if (isInParentDiv) {
      this.setState((state) => ({
        ...state,
        isFocused: false,
      }))
      return
    }
    const textAreaContents = this.textArea.value
    if (this.state.isEditing || this.state.isNewAnnotation) {
      this.setState(
        (state) => ({ ...state, isEditing: false, isFocused: false }),
        () => {
          if (this.state.isNewAnnotation && !textAreaContents.length) {
            this.props.onDeleteAnnotation(this.props.annotationPage, this.props.annotationID)
          } else if (
            this.props.canEdit &&
            this.props.annotationContents !== textAreaContents &&
            textAreaContents
          ) {
            this.props.onAnnotationSave(textAreaContents, {
              clientHeight: this.textArea.clientHeight,
              clientWidth: this.textArea.clientWidth,
            })
          } else {
            this.textArea.value = this.props.annotationContents
          }
        },
      )
    }
  }

  handlePointerDown = (_event) => {
    this.textArea.focus()

    this.setState((state) => ({
      ...state,
      isFocused: true,
      isEditing: this.props.canEdit,
    }))
  }

  handleKeyDown = (event) => {
    if (!event.shiftKey && event.key === 'Enter') {
      event.preventDefault()
      this.handleAnnotationSave()
      const FreetextGroup = this.div.closest('.FreetextGroup')
      if (FreetextGroup instanceof HTMLElement) {
        FreetextGroup && FreetextGroup.focus()
      }
    }
  }

  handleChange = () => {
    this.setState((state) => ({
      ...state,
      isEditing: true,
    }))
  }

  handleMouseEnter = () => {
    this.props.onTextareaMouseEnter(true)
  }

  handleMouseLeave = () => {
    this.props.onTextareaMouseEnter(false)
  }

  setTextareaWidth = () => {
    const selectionBoxPadding = 15
    const x1 = this.props.x
    const maxWidth = this.props.viewportWidth - x1 - selectionBoxPadding

    if (this.textArea instanceof HTMLElement) {
      this.textArea.style.width = ''
      this.textArea.style.whiteSpace = ''
      this.textArea.style.wordWrap = 'normal' // IE needs a normal word wrap for initial width
      this.textArea.style.width = `${Math.min(maxWidth, this.textArea.scrollWidth)}px`
      this.textArea.style.wordWrap = 'break-word'
      this.textArea.style.whiteSpace = this.textArea.scrollWidth > maxWidth ? 'pre-line' : 'pre'
    }
  }

  updateTextareaHeight = () => {
    const selectionBoxPadding = 15
    const y2 = this.props.y
    const maxHeight = this.props.viewportHeight - y2 - selectionBoxPadding
    this.textArea.style.height = ''
    this.textArea.style.height = Math.min(
      maxHeight,
      /**
       * Chrome & Chromium Edge on the Windows Surface make the textareas a tiny bit too small so it ends up
       * showing a scroll bar and doesn't let you add text. Adding 1px of padding fixes the issue and isn't noticeable
       * on browsers that don't have this problem.
       */
      this.textArea.scrollHeight + 1,
    )
    this.textArea.style['overflow-y'] = 'auto'
  }

  updateTextareaDimension = (event) => {
    event.preventDefault()
    this.setTextareaWidth()
    this.updateTextareaHeight()
  }

  render() {
    const scaledFont = `${this.props.annotationFontSize * this.props.scaleModifier}pt`

    return (
      <div className="Freetext-content" ref={this.setDivRef}>
        <textarea
          rows="1"
          cols="1"
          aria-label={this.props.annotationUserName}
          readOnly={!this.props.canEdit || this.props.isDragging}
          spellCheck={this.state.isEditing}
          className="Freetext-textarea"
          style={{
            fontSize: scaledFont,
            color: this.props.annotationColor,
            backgroundColor: this.props.annotationFillColor,
            border:
              !this.state.isFocused && this.props.isSelected && '1px solid rgba(0, 0, 0, 0.25)',
          }}
          ref={this.setTextAreaRef}
          onMouseEnter={this.handleMouseEnter}
          onMouseLeave={this.handleMouseLeave}
          onPointerDown={this.handlePointerDown}
          onKeyDown={this.handleKeyDown}
          onChange={this.handleChange}
          onInput={this.updateTextareaDimension}
          defaultValue={this.props.annotationContents}
        />
      </div>
    )
  }
}

FreetextEntry.propTypes = {
  annotationColor: PropTypes.string,
  annotationContents: PropTypes.string,
  annotationFillColor: PropTypes.string,
  annotationFont: PropTypes.string,
  annotationID: PropTypes.string,
  annotationPage: PropTypes.number,
  annotationUserName: PropTypes.string,
  canEdit: PropTypes.bool,
  isDragging: PropTypes.bool,
  isSelected: PropTypes.bool,
  onAnnotationSave: PropTypes.func,
  onDeleteAnnotation: PropTypes.func,
  onParentBlur: PropTypes.func,
  onSelectAnnotation: PropTypes.func,
  onTextareaMouseEnter: PropTypes.func,
  viewportHeight: PropTypes.number,
  viewportWidth: PropTypes.number,
  x: PropTypes.number,
  y: PropTypes.number,
}
